import { copy } from '@ohear/shared'
import { Button, Checkbox, ProgressBar } from '@ohear/ui'
import { Link, createFileRoute } from '@tanstack/react-router'
/**
 * Script review (formerly "story mode") tab.
 *
 * Two-column layout: full-episode HLS video player on the left, scene
 * navigation + my-line jump on the right. Read-only — no recording happens
 * here.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useBatchCues } from '../../../lib/api/cues'
import { useActorProject } from '../../../lib/api/projects'
import { useRequireRole } from '../../../lib/auth'
import { useT } from '../../../lib/i18n/use-t'

export const Route = createFileRoute('/actor/projects/$projectId/script-review')({
  component: ActorScriptReviewPage,
})

function ActorScriptReviewPage() {
  useRequireRole(['actor'])
  const t = useT()
  const { projectId } = Route.useParams()
  const project = useActorProject(projectId)
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const [currentTime, setCurrentTime] = useState(0)
  const [highlightMine, setHighlightMine] = useState(true)
  const [showAll, setShowAll] = useState(true)

  // Track playback time so the subtitle line updates in sync.
  useEffect(() => {
    const el = videoRef.current
    if (!el) return
    const onTime = () => setCurrentTime(el.currentTime)
    el.addEventListener('timeupdate', onTime)
    return () => el.removeEventListener('timeupdate', onTime)
  }, [project.data])

  const activeBatch = useMemo(() => {
    if (!project.data) return null
    return (
      project.data.batches.find((b) => b.status === 'in_progress') ??
      project.data.batches[0] ??
      null
    )
  }, [project.data])
  const cuesQ = useBatchCues(projectId, activeBatch?.batch_id ?? null)
  const cues = cuesQ.data?.cues ?? []
  const scenes = cuesQ.data?.scenes ?? []

  if (project.isLoading) return <p className="text-muted p-6">Loading project…</p>
  if (project.isError || !project.data) {
    return (
      <div className="p-6">
        <p className="text-amber-400">{t(copy.common.NOT_FOUND.load_project)}</p>
        <Link to="/actor" className="text-blue-400 hover:underline">
          Back to projects
        </Link>
      </div>
    )
  }
  const data = project.data

  // Find the line that overlaps with currentTime.
  const currentLine = cues.find((c) => c.in_sec <= currentTime && currentTime <= c.out_sec) ?? null
  const visibleLines = showAll ? cues : cues.filter((c) => c.is_mine)

  const jumpToMyNext = () => {
    const next = cues.find((c) => c.is_mine && c.in_sec > currentTime)
    if (next && videoRef.current) {
      videoRef.current.currentTime = next.in_sec
      void videoRef.current.play().catch(() => undefined)
    }
  }

  const totalSec = data.duration_sec || 1
  const playedPct = (currentTime / totalSec) * 100

  return (
    <section className="bg-bg" style={{ minHeight: 'calc(100vh - 80px)' }}>
      {/* Sub-nav with tab switcher. Wraps on small screens so the tab pair
          can drop to a second row instead of overflowing horizontally. */}
      <div
        className="panel sticky top-0 z-10 flex flex-wrap items-center justify-between gap-2 border-b px-4 sm:px-6 py-2 text-sm"
        style={{ borderColor: 'var(--border)' }}
      >
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 min-w-0">
          <Link to="/actor" className="text-blue-400 hover:underline">
            ← Projects
          </Link>
          <span className="text-muted hidden sm:inline">|</span>
          <span className="text-faded truncate">{data.client_name}</span>
          <span className="text-muted hidden sm:inline">|</span>
          <span className="truncate">
            {data.characters.map((c, i) => (
              <span key={c.character_id}>
                {i > 0 ? <span className="text-muted"> / </span> : null}
                <span className="text-blue-400">[{c.display_name}]</span>{' '}
                <span className="text-faded font-mono">{c.character_code}</span>
              </span>
            ))}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/actor/projects/$projectId/script-review"
            params={{ projectId }}
            className="tab active border-blue-500 px-2 pb-1 text-sm"
            style={{ borderColor: 'var(--accent)', color: 'var(--accent)', borderBottomWidth: 2 }}
          >
            📖 Script review
          </Link>
          <Link
            to="/actor/projects/$projectId/record"
            params={{ projectId }}
            className="tab text-muted border-b-2 border-transparent px-2 pb-1 text-sm"
          >
            🎤 Record
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 p-4 sm:p-6">
        {/* Video + transport — full-width on mobile, 8/12 on tablet+ */}
        <div className="md:col-span-8">
          <div className="card aspect-video overflow-hidden rounded-lg">
            <video
              ref={videoRef}
              controls
              playsInline
              src={data.story_mode_hls_url ?? undefined}
              className="h-full w-full"
              aria-label={`${data.title} full episode`}
            />
          </div>
          <div className="mt-3 flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                const el = videoRef.current
                if (!el) return
                if (el.paused) void el.play()
                else el.pause()
              }}
            >
              ▶ / ⏸
            </Button>
            <span className="text-muted text-xs font-mono">
              {formatHMS(currentTime)} / {formatHMS(totalSec)}
            </span>
            <div className="flex-1">
              <ProgressBar value={playedPct} />
            </div>
          </div>
          <div className="card mt-3 rounded p-3">
            <div className="text-muted mb-1 text-xs">Subtitles</div>
            <div>
              {currentLine ? (
                currentLine.is_mine ? (
                  <span className="text-blue-400 font-bold">
                    [{currentLine.character_code} (you)] {currentLine.line_text}
                  </span>
                ) : (
                  <span className="text-muted">
                    [{currentLine.character_code}] {currentLine.line_text}
                  </span>
                )
              ) : (
                <span className="text-faded">—</span>
              )}
            </div>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-3 sm:gap-4 text-sm">
            <Button variant="ghost" size="sm" onClick={jumpToMyNext}>
              📍 Jump to my next line
            </Button>
            <Checkbox
              label="Highlight my character"
              checked={highlightMine}
              onChange={(e) => setHighlightMine(e.currentTarget.checked)}
            />
            <Checkbox
              label="Show all dialogue"
              checked={showAll}
              onChange={(e) => setShowAll(e.currentTarget.checked)}
            />
          </div>
        </div>

        {/* Scene chapters / lines — full-width on mobile, 4/12 on tablet+ */}
        <div className="md:col-span-4">
          <div className="text-muted mb-2 text-xs">Scenes</div>
          <div className="space-y-1">
            {scenes.map((s) => {
              const sceneCues = cues.filter((c) => c.scene_number === s.scene_number)
              const start = sceneCues[0]?.in_sec ?? 0
              const end = sceneCues[sceneCues.length - 1]?.out_sec ?? 0
              const isActive = currentTime >= start && currentTime <= end
              return (
                <button
                  type="button"
                  key={s.scene_number}
                  onClick={() => {
                    if (videoRef.current) videoRef.current.currentTime = start
                  }}
                  className="card block w-full cursor-pointer rounded px-3 py-2 text-left text-sm"
                  style={isActive ? { borderColor: 'var(--accent)' } : undefined}
                >
                  Scene {s.scene_number} · {s.label} ({formatHMS(start)} - {formatHMS(end)})
                  {isActive && ' ◀'}
                </button>
              )
            })}
          </div>

          {highlightMine && visibleLines.length > 0 && (
            <>
              <div className="text-muted mt-4 mb-2 text-xs">Lines</div>
              <ul className="space-y-1 text-sm">
                {visibleLines.slice(0, 50).map((c) => (
                  <li key={c.cue_id} className={c.is_mine ? 'text-blue-400' : 'text-muted'}>
                    [{c.character_code}] {c.line_text}
                  </li>
                ))}
              </ul>
              {visibleLines.length > 50 && (
                <p className="text-faded mt-2 text-xs">… {visibleLines.length - 50} more</p>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  )
}

function formatHMS(sec: number): string {
  const h = Math.floor(sec / 3600)
  const m = Math.floor((sec % 3600) / 60)
  const s = Math.floor(sec % 60)
  if (h > 0) return `${h}:${pad2(m)}:${pad2(s)}`
  return `${m}:${pad2(s)}`
}
function pad2(n: number): string {
  return n.toString().padStart(2, '0')
}
